import { TitleButtonDivider } from '@/common/buttons/TitleButtonDivider';
import { EntityPerspective } from '@/common/entityFramework/entityPerspective/EntityPerspective';
import { useFilterButtons } from '@/common/entityFramework/entityPerspective/hooks/useFilterButtons';
import { RowIcon } from '@/common/entityFramework/entityPerspective/support/RowIcon';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { useCallback, useRef } from 'react';

export const AbvOverUnderPerspective = ({ overUnderScenId }) => {

  const entityPerspectiveRef = useRef();

  const views = [
    {
      name: 'Full Breakout',
      dims: ['OVER_UNDER_SCEN', 'EARNED_PERIOD', 'CHANNEL', 'PROD'],
      kpis: ['LBE_ACCR_AMT', 'PRIOR_YEAR_LBE_ACCR_AMT', 'TOTAL_LBE_ACCR_AMT', 'ACTUAL_AUTO_ACCR_AMT', 'ACTUAL_MAN_ACCR_AMT', 'TOTAL_ACTUAL_ACCR_AMT', 'PYMT_AMT', 'PYMT_ADJ', 'TOTAL_PYMT', 'FORECASTED_LIAB', 'LIAB_ADJ'],
      disabledDims: ['OVER_UNDER_SCEN'],
      numFrozenDims: 1,
      dimToPeriodicities: {
        EARNED_PERIOD: ['LIFETIME', 'YEAR', 'QTR']
      }
    }
  ];

  const { filterButtonStages, filterButtons, onViewReset } = useFilterButtons([
    {
      key: 'validated',
      icon: fas.faCheckCircle,
      colors: [null, 'green', 'gry'],
      toolTips: ['Validated and not validated', 'Validated', 'Not validated'],
    }
  ]);

  const augButtons = (buttons) => {
    buttons.unshift(...filterButtons, <TitleButtonDivider key="filterButtonDivider" />);
  }

  const augReqParam = useCallback((reqParam) => {
    // Discrepancy
    const discrepancyButtonState = filterButtonStages.validated;
    if (discrepancyButtonState > 0) {
      reqParam.expr = ExprUtils.and(
        reqParam.expr,
        discrepancyButtonState === 1 ? ExprUtils.whereParams('validated', 1) : ExprUtils.isNot('validated', 1)
      )
    }
  }, [filterButtonStages]);

  const rowIconsFn = (data) => {
    let rowIcons = [];

    // Discrepancy row icons
    if (data.validated === 1) {
      rowIcons.push(<RowIcon key="validated" icon={fas.faCheckCircle} color="green" toolTip="Validated" />);
    } else if (data.validated > 0) {
      rowIcons.push(<RowIcon key="containsValidation" icon={fas.faCheckCircle} color="#90ee90" toolTip="Contains a validation" />);
    }

    return rowIcons;
  }

  const defaultDimFilters = { OVER_UNDER_SCEN: [overUnderScenId] };

  const getContextMenuItems = (params) => {
    let contextMenus = entityPerspectiveRef.current.getContextMenuItems(params);

    let viewerPlugin = 'OVER_UNDER_VIEWER_PLUGIN';
    contextMenus.push(
      {
        name: 'Auto Accrual Amounts',
        action: () => entityPerspectiveRef.current.navigateToViewer(params, 'ACCR_AMT', 'Accrual Amounts', viewerPlugin),
        icon: fas.faListUl
      },
      {
        name: 'Manual Accrual Adjustments',
        action: () => entityPerspectiveRef.current.navigateToEntityList(params, 'MAN_ACCR_ADJ', {pluginCode: 'OVER_UNDER_MAN_ACCR_ADJ_DATA_PLUGIN'}),
        icon: fas.faListUl
      },
      {
        name: 'Payment Amounts',
        action: () => entityPerspectiveRef.current.navigateToViewer(params, 'PYMT_AMT', 'Payment Amounts', viewerPlugin),
        icon: fas.faListUl
      },
      {
        name: 'Payment Adjustments',
        action: () => entityPerspectiveRef.current.navigateToViewer(params, 'PYMT_ADJ', 'Payment Adjustments', viewerPlugin),
        icon: fas.faListUl
      }, {
      name: 'Validate',
      action: () => entityPerspectiveRef.current.openActionDialog(params, 'Validate', 'OVER_UNDER_VALIDATE_ACTION_PLUGIN'),
      icon: fas.faCheckCircle
    }, {
      name: 'Export',
      action: () => entityPerspectiveRef.current.openExportDialog(params),
      icon: fas.faArrowCircleDown
    }
    );

    return contextMenus;
  }

  return (
    <EntityPerspective
      ref={entityPerspectiveRef}
      entity={'OVER_UNDER'}
      defaultDimFilters={defaultDimFilters}
      views={views}
      rowIconsFn={rowIconsFn}
      augButtons={augButtons}
      augReqParam={augReqParam}
      onViewReset={onViewReset}
      gridOptions={{
        getContextMenuItems
      }}
    />
  )
}